import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { QrCode, Package, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to format BOM quantity display (no conversion needed - everything is in native units)
const formatBOMQuantity = (qty, unit, itemType) => {
  return `${qty.toFixed(2)} ${unit}`;
};

const LogPickModal = ({ isOpen, onClose, workOrder, component, onSuccess }) => {
  const [lotNumber, setLotNumber] = useState("");
  const [quantity, setQuantity] = useState("");
  const [loading, setLoading] = useState(false);
  const [recentPicks, setRecentPicks] = useState([]);
  const [fetchingLots, setFetchingLots] = useState(false);
  const [availableLots, setAvailableLots] = useState([]);
  const [selectedLotData, setSelectedLotData] = useState(null);

  useEffect(() => {
    if (isOpen && component) {
      // Get recent picks from all lots
      const picks = [];
      component.lots?.forEach(lot => {
        lot.picks?.forEach(pick => {
          picks.push({
            ...pick,
            lot_number: lot.lot_number
          });
        });
      });
      // Sort by picked_at descending (most recent first)
      picks.sort((a, b) => new Date(b.picked_at) - new Date(a.picked_at));
      setRecentPicks(picks.slice(0, 5)); // Show last 5 picks
    }
  }, [isOpen, component]);

  // Fetch valid lots from NetSuite via Python Backend when modal opens
  useEffect(() => {
    if (isOpen && component?.component_id) {
      fetchValidLots();
    } else {
      // Reset lot selection state on close
      setAvailableLots([]);
      if (!isOpen) {
          setLotNumber("");
          setQuantity("");
          setSelectedLotData(null);
      }
    }
  }, [isOpen, component]);

  const fetchValidLots = async () => {
    setFetchingLots(true);
    try {
      // Calls our new Python endpoint which talks to NetSuite RESTlet
      const response = await axios.get(`${API}/inventory/lots/${component.component_id}`);
      
      if (Array.isArray(response.data)) {
        // Sort by Expiration Date (Oldest First - FEFO)
        const sortedLots = response.data.sort((a, b) => {
            if (!a.expirationDate) return 1;
            if (!b.expirationDate) return -1;
            return new Date(a.expirationDate) - new Date(b.expirationDate);
        });
        setAvailableLots(sortedLots);
      }
    } catch (error) {
      console.error("Error fetching lots:", error);
      toast.error("Could not fetch inventory lots from NetSuite.");
    } finally {
      setFetchingLots(false);
    }
  };

  const handleLotChange = (e) => {
    const selectedLotNum = e.target.value;
    setLotNumber(selectedLotNum);
    
    // Find full lot data to check availability
    const lotData = availableLots.find(l => l.lotNumber === selectedLotNum);
    setSelectedLotData(lotData || null);
  };

  const handleScanQR = () => {
    // Placeholder for QR code scanning
    toast.info("QR Code scanning will be implemented with WMS/NetSuite integration");
    // For now, you could set a dummy pallet ID
    setLotNumber("PALLET-" + Math.random().toString(36).substring(2, 10).toUpperCase());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!lotNumber || !quantity || parseFloat(quantity) <= 0) {
      toast.error("Please enter valid lot number and quantity");
      return;
    }

    // Validation: Don't allow picking more than available
    if (selectedLotData && parseFloat(quantity) > selectedLotData.quantityAvailable) {
        toast.error(`Insufficient inventory. Only ${selectedLotData.quantityAvailable} available in this lot.`);
        return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/production/${workOrder.wo_id}/log-pick`, null, {
        params: {
          component_id: component.component_id,
          lot_number: lotNumber,
          quantity: parseFloat(quantity),
          picked_by: "warehouse"
        }
      });
      
      toast.success("Pick logged successfully");
      setLotNumber("");
      setQuantity("");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error logging pick:", error);
      toast.error(error.response?.data?.detail || "Failed to log pick");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Log Pick - {component?.component_name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Lot Selection Dropdown */}
          <div className="space-y-2">
            <Label htmlFor="lot-select">Select Lot Number (NetSuite)</Label>
            {fetchingLots ? (
                <div className="flex items-center gap-2 text-sm text-gray-500 border p-2 rounded bg-gray-50">
                    <Loader2 className="w-4 h-4 animate-spin" /> Fetching live inventory...
                </div>
            ) : (
                <select
                    id="lot-select"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={lotNumber}
                    onChange={handleLotChange}
                    disabled={availableLots.length === 0}
                >
                    <option value="">-- Select a Lot --</option>
                    {availableLots.map((lot, idx) => (
                        <option key={`${lot.lotNumber}-${idx}`} value={lot.lotNumber}>
                            {lot.lotNumber} | Qty: {lot.quantityAvailable} {component?.unit} {lot.expirationDate ? `| Exp: ${lot.expirationDate}` : ''}
                        </option>
                    ))}
                </select>
            )}
            
            {/* Availability / Error Message */}
            {!fetchingLots && availableLots.length === 0 && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> No available inventory found in NetSuite.
                </p>
            )}
            
            {/* Selected Lot Info */}
            {selectedLotData && (
                <div className="text-xs bg-green-50 text-green-800 p-2 rounded border border-green-200">
                    <strong>Selected:</strong> {selectedLotData.lotNumber}<br/>
                    <strong>Available:</strong> {selectedLotData.quantityAvailable} {component?.unit}<br/>
                    <strong>Bin:</strong> {selectedLotData.binNumber || 'N/A'}
                </div>
            )}
          </div>

          {/* Quantity Input */}
          <div>
            <Label htmlFor="quantity">Quantity ({component?.unit})</Label>
            <Input
              id="quantity"
              type="number"
              step="0.01"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Enter quantity picked"
              required
            />
          </div>

          {/* Recent Pick History */}
          {recentPicks.length > 0 && (
            <div>
              <Label className="mb-2 block">Recent Picks</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {recentPicks.map((pick, idx) => (
                  <Card key={idx} className="bg-gray-50">
                    <CardContent className="p-3">
                      <div className="flex justify-between items-start text-sm">
                        <div>
                          <div className="font-mono font-semibold">{pick.lot_number}</div>
                          <div className="text-xs text-gray-600">
                            {formatBOMQuantity(pick.picked_qty, component?.unit, component?.item_type)} • {pick.picked_by}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(pick.picked_at).toLocaleString()}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
                type="submit" 
                disabled={loading || !lotNumber || !quantity || (selectedLotData && parseFloat(quantity) > selectedLotData.quantityAvailable)}
            >
              {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Logging...
                  </>
              ) : "Log Pick"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LogPickModal;
